/**
 * API base URL for brand-admin backend requests.
 *
 * Priority:
 * 1. REACT_APP_BRAND_ADMIN_API_URL (build-time, brand-admin-specific)
 * 2. REACT_APP_API_URL / REACT_APP_API_BASE_URL (shared)
 * 3. window.REACT_APP_BRAND_ADMIN_API_URL (injected)
 * 4. Dev fallback on localhost:3003 -> localhost:5000 (brand-admin backend)
 * 5. window.location.origin
 */
const DEFAULT_DEV_API_ORIGIN = 'http://localhost:5000';
const PRODUCTION_CUSTOMER_HOST = 'analyze.nyafaz.com';
const PRODUCTION_BRAND_API_PATH = '/brand-admin-api';

function productionBrandApiOriginForHost(origin) {
  try {
    const url = new URL(origin);
    const host = String(url.hostname || '').toLowerCase();
    if (host === PRODUCTION_CUSTOMER_HOST) {
      return `${url.origin}${PRODUCTION_BRAND_API_PATH}`;
    }
  } catch {
    // ignore invalid origin
  }
  return null;
}

function isBrandAdminDevOrigin(origin) {
  if (!origin) return false;
  try {
    const { hostname, port } = new URL(origin);
    const isLocalHost =
      hostname === 'localhost' ||
      hostname === '127.0.0.1' ||
      hostname === '[::1]' ||
      hostname.endsWith('.local');
    return isLocalHost && (port === '3000' || port === '3003' || port === '5173' || port === '');
  } catch {
    return false;
  }
}

export function getBrandAdminApiBaseUrl() {
  const fromBrandEnv =
    typeof process !== 'undefined' && process.env && process.env.REACT_APP_BRAND_ADMIN_API_URL;
  if (fromBrandEnv && String(fromBrandEnv).trim()) {
    return String(fromBrandEnv).trim().replace(/\/$/, '');
  }

  const fromEnv =
    (typeof process !== 'undefined' && process.env && process.env.REACT_APP_API_URL) ||
    (typeof process !== 'undefined' && process.env && process.env.REACT_APP_API_BASE_URL);
  const normalizedFromEnv = fromEnv && String(fromEnv).trim() ? String(fromEnv).trim().replace(/\/$/, '') : '';

  if (typeof window !== 'undefined') {
    const fromWindow = window.REACT_APP_BRAND_ADMIN_API_URL || window.REACT_APP_API_URL;
    if (fromWindow && String(fromWindow).trim()) {
      return String(fromWindow).trim().replace(/\/$/, '');
    }

    const origin = window.location && window.location.origin;
    if (isBrandAdminDevOrigin(origin)) {
      return DEFAULT_DEV_API_ORIGIN;
    }

    if (origin) {
      try {
        const mapped = productionBrandApiOriginForHost(origin);
        if (mapped) return mapped;
      } catch {
        // ignore invalid origin
      }
    }

    if (normalizedFromEnv) return normalizedFromEnv;
    if (origin) return origin;
  }

  if (normalizedFromEnv) return normalizedFromEnv;

  if (typeof window !== 'undefined' && window.location?.origin) {
    const mapped = productionBrandApiOriginForHost(window.location.origin);
    if (mapped) return mapped;
  }

  return DEFAULT_DEV_API_ORIGIN;
}
